import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../config/supabase";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import {
    FiArrowRight,
    FiCheckCircle,
    FiClock,
    FiExternalLink,
    FiInfo,
    FiLock,
    FiShield,
    FiTrash2,
    FiZap,
} from "react-icons/fi";

import TextTooltip from "../common/TextTooltip";
import IntegrationXeroSetup from "../modals/guides/integrationXeroSetup";
import DropdownSection from "../common/DropdownSection";
import PricingQuotationSettings from "./sections/PricingQuotationSettings";

const XERO_CONNECT_FUNCTION_URL =
    "https://xgezccwtibbfoplffzcr.supabase.co/functions/v1/xero-connect";

const NOTIFY_FRIDAY_INVOICE_STATES = [
    { value: null, label: "No Value / Empty" },
    { value: "not_invoiced", label: "Not Invoiced" },
    { value: "pending", label: "Pending" },
    { value: "invoiced", label: "Invoiced" },
    { value: "deposit", label: "Deposit" },
    { value: "cod", label: "COD" },
    { value: "paid", label: "Paid" },
];

const XERO_FRIDAY_INVOICE_STATES = [
    { value: "DRAFT", label: "Draft" },
    { value: "SUBMITTED", label: "Submitted / Needs Approval" },
    { value: "AUTHORISED", label: "Authorised / Invoiced" },
    { value: "PAID", label: "Paid" },
    { value: "VOIDED", label: "Voided / Cancelled" },
    { value: "DELETED", label: "Deleted" },
];

const ROLE_OPTIONS = [
    { key: "super_admin", label: "Super Admin" },
    { key: "admin", label: "Admin" },
    { key: "estimator", label: "Estimator" },
    { key: "designer", label: "Designer" },
    { key: "driver", label: "Driver" },
];

const RESTRICTED_UPLOAD_FOLDERS = ["Quotes & Invoices", "Layouts & Comps"];

const getDefaultFridayInvoiceOptions = () => ({
    notify: [null, "not_invoiced"],
    xero: [null],
});

const SectionCard = ({ title, description, icon, children, right }) => (
    <section className="settings-section rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
                {icon && (
                    <div className="w-10 h-10 rounded-2xl bg-zinc-100 text-zinc-700 flex items-center justify-center shrink-0">
                        {icon}
                    </div>
                )}

                <div>
                    <h2 className="text-lg font-semibold text-zinc-900">
                        {title}
                    </h2>
                    {description && (
                        <p className="text-sm text-gray-500 mt-1 max-w-3xl">
                            {description}
                        </p>
                    )}
                </div>
            </div>

            {right}
        </div>

        <div className="p-5">
            {children}
        </div>
    </section>
);

const SmallStat = ({ label, value, tone = "neutral" }) => {
    const tones = {
        neutral: "bg-white/10 border-white/10 text-white",
        green: "bg-green-50 border-green-100 text-green-700",
        amber: "bg-amber-50 border-amber-100 text-amber-700",
        zinc: "bg-zinc-50 border-zinc-100 text-zinc-700",
    };

    return (
        <div className={`rounded-2xl border px-4 py-3 ${tones[tone]}`}>
            <p className="text-[11px] opacity-70">{label}</p>
            <p className="mt-1 text-sm font-semibold truncate">{value || "—"}</p>
        </div>
    );
};

const TogglePill = ({ selected, disabled, onClick, children, tone = "zinc" }) => {
    const active = {
        zinc: "border-zinc-900 bg-zinc-900 text-white",
        sky: "border-sky-600 bg-sky-600 text-white",
        green: "border-green-600 bg-green-600 text-white",
    };

    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed ${selected
                ? active[tone]
                : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                }`}
        >
            {children}
        </button>
    );
};

const Settings = () => {
    const { company, userProfile } = useAuth();

    const [loading, setLoading] = useState(true);
    const [xeroConnection, setXeroConnection] = useState(null);
    const [error, setError] = useState(null);

    const [invoiceDisplayMode, setInvoiceDisplayMode] = useState(
        company?.settings?.invoice_display_mode || "notify"
    );

    const [invoiceTextMode, setInvoiceTextMode] = useState(
        company?.settings?.invoice_text_mode || "friendly"
    );

    const [fridayInvoiceOptions, setFridayInvoiceOptions] = useState(
        company?.settings?.friday_invoice_options || getDefaultFridayInvoiceOptions()
    );

    const defaultRestrictedFolderUploadRoles = {
        "Quotes & Invoices": ["admin", "super_admin"],
        "Layouts & Comps": ["admin", "super_admin"],
    };

    const [restrictedFolderUploadRoles, setRestrictedFolderUploadRoles] = useState(
        company?.settings?.restricted_folder_upload_roles || defaultRestrictedFolderUploadRoles
    );

    const [defaultEstimateDate, setDefaultEstimateDate] = useState(
        company?.settings?.default_estimate_date ?? ""
    );

    const [allowAnyUserToActAsEstimator, setAllowAnyUserToActAsEstimator] = useState(
        !!company?.settings?.allow_any_user_to_act_as_estimator
    );

    const [enableDriverFiles, setEnableDriverFiles] = useState(
        !!company?.settings?.enable_driver_files
    );

    const [companyUsers, setCompanyUsers] = useState([]);
    const [weeklyInvoiceUsers, setWeeklyInvoiceUsers] = useState([]);
    const [loadingWeeklyInvoiceUsers, setLoadingWeeklyInvoiceUsers] = useState(false);

    const [savingCompanySettings, setSavingCompanySettings] = useState(false);
    const [disconnectingXero, setDisconnectingXero] = useState(false);
    const [showXeroGuide, setShowXeroGuide] = useState(false);

    const isSuperAdmin = userProfile?.role === "super_admin";
    const permissions = userProfile?.permissions || [];

    const canConnectXero =
        permissions.includes("CONNECT_INTEGRATIONS") &&
        permissions.includes("CONNECT_INTEGRATION_XERO");

    const companyInitial = useMemo(() => {
        return (
            company?.name?.[0] ||
            company?.company_name?.[0] ||
            company?.slug?.[0] ||
            "N"
        ).toUpperCase();
    }, [company]);

    const companyName =
        company?.name ||
        company?.company_name ||
        company?.slug ||
        "Company Settings";

    const isXeroConnected = xeroConnection?.status === "connected";

    const fetchXeroConnection = async () => {
        if (!company?.id) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const { data, error } = await supabase
                .from("xero_connections")
                .select(`
                    id,
                    company_id,
                    xero_tenant_id,
                    xero_tenant_name,
                    status,
                    scopes,
                    expires_at,
                    connected_by,
                    created_at,
                    updated_at
                `)
                .eq("company_id", company.id)
                .maybeSingle();

            if (error) throw error;

            setXeroConnection(data);
        } catch (err) {
            console.error("Failed to fetch Xero connection:", err);
            setError(err.message || "Failed to load Xero connection");
        } finally {
            setLoading(false);
        }
    };

    const fetchWeeklyInvoiceUsers = async () => {
        if (!company?.id) return;

        try {
            setLoadingWeeklyInvoiceUsers(true);

            const { data, error } = await supabase
                .from("users")
                .select("id, email, username, full_name, role, avatar_url, position")
                .eq("company_id", company.id)
                .eq("is_deleted", false)
                .contains("permissions", ["GET_WEEKLY_INV_RECORDS"])
                .order("role", { ascending: true })
                .order("full_name", { ascending: true });

            if (error) throw error;

            setWeeklyInvoiceUsers(data || []);
        } catch (err) {
            console.error("Failed to fetch weekly invoice users:", err);
        } finally {
            setLoadingWeeklyInvoiceUsers(false);
        }
    };

    const fetchCompanyUsers = async () => {
        if (!company?.id) return;

        const { data, error } = await supabase
            .from("users")
            .select(`
            id,
            email,
            username,
            full_name,
            role,
            avatar_url,
            position,
            permissions
        `)
            .eq("company_id", company.id)
            .eq("is_deleted", false)
            .order("full_name", { ascending: true });

        if (error) {
            console.error("Failed to fetch company users:", error);
            return;
        }

        setCompanyUsers(
            (data || []).map(user => ({
                ...user,
                permissions: Array.isArray(user.permissions)
                    ? user.permissions
                    : [],
            }))
        );
    };

    useEffect(() => {
        if (!company?.id) {
            setLoading(false);
            return;
        }

        fetchXeroConnection();
        fetchWeeklyInvoiceUsers();
        fetchCompanyUsers();
    }, [company?.id]);

    useEffect(() => {
        const handleMessage = async (event) => {
            if (event.origin !== window.location.origin) return;

            if (
                event.data?.type === "INTEGRATION_CONNECTED" &&
                event.data?.integration === "xero" &&
                event.data?.status === "success"
            ) {
                setInvoiceDisplayMode("hybrid");

                await updateCompanySettings(
                    { invoice_display_mode: "hybrid" },
                    "Xero connected and invoice display mode set to hybrid"
                );

                fetchXeroConnection();
            }
        };

        window.addEventListener("message", handleMessage);

        return () => window.removeEventListener("message", handleMessage);
    }, []);

    const updateCompanySettings = async (
        patch,
        logDetails = "Company settings updated"
    ) => {
        if (!company?.id) return;

        try {
            setSavingCompanySettings(true);
            setError(null);

            const nextSettings = {
                ...(company?.settings || {}),
                ...patch,
            };

            const { error } = await supabase
                .from("companies")
                .update({ settings: nextSettings })
                .eq("id", company.id);

            if (error) throw error;

            await supabase.from("job_action_logs").insert({
                job_id: null,
                company_id: company.id,
                user_id: userProfile?.id || null,
                user_name:
                    userProfile?.full_name ||
                    userProfile?.username ||
                    userProfile?.email ||
                    "Unknown",
                user_role: userProfile?.role || null,
                action: "company_settings_updated",
                details: logDetails,
                changed_fields: Object.keys(patch),
                old_values: company?.settings || {},
                new_values: nextSettings,
                action_origin: "settings_page",
            });
        } catch (err) {
            console.error("Failed to update company settings:", err);
            setError(err.message || "Failed to update company settings");
        } finally {
            setSavingCompanySettings(false);
        }
    };

    const handleToggleAllowAnyUserEstimator = async (checked) => {
        if (!isSuperAdmin) return;

        setAllowAnyUserToActAsEstimator(checked);

        await updateCompanySettings(
            { allow_any_user_to_act_as_estimator: checked },
            `Allow any user to act as estimator ${checked ? "enabled" : "disabled"}`
        );
    };

    const handleToggleDriverFiles = async (checked) => {
        if (!isSuperAdmin) return;

        setEnableDriverFiles(checked);

        await updateCompanySettings(
            { enable_driver_files: checked },
            `Driver Files ${checked ? "enabled" : "disabled"}`
        );
    };

    const toggleRestrictedFolderUploadRole = async (folderName, role) => {
        if (userProfile?.role !== "super_admin") return;

        const currentSettings = restrictedFolderUploadRoles || defaultRestrictedFolderUploadRoles;

        const currentRoles = currentSettings[folderName] || [];
        const nextRoles = currentRoles.includes(role)
            ? currentRoles.filter(r => r !== role)
            : [...currentRoles, role];

        const nextRestrictedFolderUploadRoles = {
            ...currentSettings,
            [folderName]: nextRoles,
        };

        setRestrictedFolderUploadRoles(nextRestrictedFolderUploadRoles);

        await updateCompanySettings(
            { restricted_folder_upload_roles: nextRestrictedFolderUploadRoles },
            `Restricted folder upload roles updated for ${folderName}`
        );
    };

    const handleSaveDefaultEstimateDate = async () => {
        if (!isSuperAdmin) return;

        const days = Number(defaultEstimateDate);

        if (!Number.isFinite(days) || days <= 0) {
            setError("Default estimate date must be more than 0 days.");
            return;
        }

        await updateCompanySettings(
            { default_estimate_date: days },
            `Default estimate date set to ${days} day(s)`
        );
    };

    const handleRemoveDefaultEstimateDate = async () => {
        if (!isSuperAdmin) return;

        const nextSettings = { ...(company?.settings || {}) };
        delete nextSettings.default_estimate_date;

        try {
            setSavingCompanySettings(true);
            setError(null);

            const { error } = await supabase
                .from("companies")
                .update({ settings: nextSettings })
                .eq("id", company.id);

            if (error) throw error;

            await supabase.from("job_action_logs").insert({
                job_id: null,
                company_id: company.id,
                user_id: userProfile?.id || null,
                user_name:
                    userProfile?.full_name ||
                    userProfile?.username ||
                    userProfile?.email ||
                    "Unknown",
                user_role: userProfile?.role || null,
                action: "company_settings_updated",
                details: "Default estimate date removed",
                changed_fields: ["default_estimate_date"],
                old_values: company?.settings || {},
                new_values: nextSettings,
                action_origin: "settings_page",
            });

            setDefaultEstimateDate("");
        } catch (err) {
            console.error("Failed to remove default estimate date:", err);
            setError(err.message || "Failed to remove default estimate date");
        } finally {
            setSavingCompanySettings(false);
        }
    };

    const handleFridayInvoiceOptionToggle = async (source, value) => {
        if (!isSuperAdmin) return;

        const currentOptions = fridayInvoiceOptions || getDefaultFridayInvoiceOptions();
        const currentValues = currentOptions[source] || [];

        const exists = currentValues.some(item => item === value);

        const nextValues = exists
            ? currentValues.filter(item => item !== value)
            : [...currentValues, value];

        const nextOptions = {
            ...currentOptions,
            [source]: nextValues,
        };

        setFridayInvoiceOptions(nextOptions);

        await updateCompanySettings(
            { friday_invoice_options: nextOptions },
            "Friday invoice popup options updated"
        );
    };

    const isFridayInvoiceOptionSelected = (source, value) => {
        const values = fridayInvoiceOptions?.[source] || [];
        return values.some(item => item === value);
    };

    const handleInvoiceDisplayModeChange = async (value) => {
        if (!isXeroConnected && ["xero", "hybrid"].includes(value)) return;

        setInvoiceDisplayMode(value);
        await updateCompanySettings(
            { invoice_display_mode: value },
            `Invoice display mode changed to ${value}`
        );
    };

    const handleInvoiceTextModeChange = async (value) => {
        if (!isXeroConnected && value === "xero") return;

        setInvoiceTextMode(value);
        await updateCompanySettings(
            { invoice_text_mode: value },
            `Invoice text mode changed to ${value}`
        );
    };

    const handleConnectXero = () => {
        if (!canConnectXero || !company?.id || !userProfile?.id) return;

        const url = new URL(XERO_CONNECT_FUNCTION_URL);
        url.searchParams.set("company_id", company.id);
        url.searchParams.set("user_id", userProfile.id);

        const popup = window.open(
            url.toString(),
            "xeroConnect",
            "width=620,height=760,left=200,top=80"
        );

        const timer = setInterval(() => {
            if (popup?.closed) {
                clearInterval(timer);
                fetchXeroConnection();
            }
        }, 700);
    };

    const handleDisconnectXero = async () => {
        if (!canConnectXero || !company?.id || !xeroConnection?.id) return;

        const confirmed = window.confirm(
            "Disconnect this company from Xero? Invoice statuses will stop syncing."
        );

        if (!confirmed) return;

        try {
            setDisconnectingXero(true);
            setError(null);

            const { error } = await supabase
                .from("xero_connections")
                .update({
                    status: "revoked",
                    updated_at: new Date().toISOString(),
                })
                .eq("id", xeroConnection.id)
                .eq("company_id", company.id);

            if (error) throw error;

            setXeroConnection(null);
            setInvoiceDisplayMode("notify");
            setInvoiceTextMode("friendly");

            await updateCompanySettings(
                {
                    invoice_display_mode: "notify",
                    invoice_text_mode: "friendly",
                },
                "Xero disconnected and invoice settings reset"
            );
        } catch (err) {
            console.error("Failed to disconnect Xero:", err);
            setError(err.message || "Failed to disconnect Xero");
        } finally {
            setDisconnectingXero(false);
        }
    };

    if (loading) {
        return (
            <div className="settings-page space-y-6">
                <Skeleton height={170} borderRadius={28} />
                <Skeleton height={360} borderRadius={28} />
                <Skeleton height={260} borderRadius={28} />
            </div>
        );
    }

    return (
        <div className="settings-page space-y-6">
            <section className="settings-header relative overflow-hidden rounded-3xl bg-zinc-950 text-white border border-zinc-800 shadow-sm">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,255,215,0.18),transparent_50%),radial-gradient(circle_at_bottom_left,rgba(0,138,247,0.16),transparent_55%)]" />

                <div className="relative p-6 sm:p-8">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white text-zinc-950 flex items-center justify-center text-xl font-semibold shadow-sm">
                                {companyInitial}
                            </div>

                            <div>
                                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                                    {companyName}
                                </h1>

                                <p className="mt-2 text-sm text-zinc-300 max-w-2xl">
                                    Manage company-wide automation, pricing rules, invoice behaviour, and integrations.
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 lg:min-w-[360px]">
                            <SmallStat label="Company ID" value={company?.id?.slice(0, 8)} />
                            <SmallStat label="Plan" value={company?.subscription_plan || "Standard"} />
                            <SmallStat label="Xero" value={isXeroConnected ? "Connected" : "Not connected"} />
                        </div>
                    </div>
                </div>
            </section>

            {error && (
                <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-sm text-red-700">
                    {error}
                </div>
            )}

            {isSuperAdmin && (
                <SectionCard
                    title="Automation"
                    description="Control default dates and scheduled review behaviour."
                    icon={<FiZap />}
                    right={
                        savingCompanySettings ? (
                            <span className="text-xs font-semibold text-gray-400">
                                Saving...
                            </span>
                        ) : null
                    }
                >
                    <div className="space-y-4">
                        <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                                <div>
                                    <h3 className="text-base font-semibold text-zinc-900">
                                        Default estimate date
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                                        When enabled, new jobs without department dates will show a confirmation popup and apply this offset from today.
                                    </p>

                                    <p className="text-xs text-gray-400 mt-3">
                                        Current value:{" "}
                                        <span className="font-semibold text-zinc-600">
                                            {company?.settings?.default_estimate_date
                                                ? `${company.settings.default_estimate_date} day(s)`
                                                : "Not enabled"}
                                        </span>
                                    </p>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                                    <div>
                                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                            Days from today
                                        </label>

                                        <input
                                            type="number"
                                            min="1"
                                            value={defaultEstimateDate}
                                            onChange={(e) => setDefaultEstimateDate(e.target.value)}
                                            placeholder="14"
                                            className="mt-2 w-full sm:w-36 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-zinc-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
                                        />
                                    </div>

                                    <button
                                        type="button"
                                        disabled={savingCompanySettings}
                                        onClick={handleSaveDefaultEstimateDate}
                                        className="px-4 py-3 rounded-2xl bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-60"
                                    >
                                        Save
                                    </button>

                                    {company?.settings?.default_estimate_date != null && (
                                        <button
                                            type="button"
                                            disabled={savingCompanySettings}
                                            onClick={handleRemoveDefaultEstimateDate}
                                            className="px-4 py-3 rounded-2xl border border-red-200 bg-red-50 text-red-700 text-sm font-semibold hover:bg-red-100 disabled:opacity-60"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <DropdownSection
                            title="Friday invoice popup"
                            subtext="Choose which invoice states should appear in the weekly invoice review popup."
                            defaultOpen={false}
                            titleStyle="text-sm font-semibold"
                            subtextStyle="text-xs text-gray-500"
                            customMainStyle="!shadow-none !border-gray-100"
                            customClickableStyle="!px-4 !py-3"
                            customContainerStyle="!p-4 !bg-gray-50"
                        >
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                <div className="rounded-2xl bg-white border border-gray-100 p-4">
                                    <h4 className="text-sm font-semibold text-zinc-800">
                                        Notify states
                                    </h4>
                                    <p className="text-xs text-gray-400 mt-1">
                                        Used in Notify mode and Hybrid mode when no Xero invoice exists.
                                    </p>

                                    <div className="flex flex-wrap gap-2 mt-4">
                                        {NOTIFY_FRIDAY_INVOICE_STATES.map((state) => (
                                            <TogglePill
                                                key={String(state.value)}
                                                selected={isFridayInvoiceOptionSelected("notify", state.value)}
                                                disabled={savingCompanySettings}
                                                onClick={() => handleFridayInvoiceOptionToggle("notify", state.value)}
                                            >
                                                {state.label}
                                            </TogglePill>
                                        ))}
                                    </div>
                                </div>

                                <div className={`rounded-2xl border p-4 ${isXeroConnected
                                    ? "bg-white border-gray-100"
                                    : "bg-gray-100 border-gray-200 opacity-70"
                                    }`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <h4 className="text-sm font-semibold text-zinc-800">
                                                Xero states
                                            </h4>
                                            <p className="text-xs text-gray-400 mt-1">
                                                Used in Xero mode and Hybrid mode when a Xero invoice exists.
                                            </p>
                                        </div>

                                        {!isXeroConnected && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-2 py-1 text-[11px] font-semibold text-gray-400">
                                                <FiLock />
                                                Requires Xero
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex flex-wrap gap-2 mt-4">
                                        {XERO_FRIDAY_INVOICE_STATES.map((state) => (
                                            <TogglePill
                                                key={state.value}
                                                tone="sky"
                                                selected={isFridayInvoiceOptionSelected("xero", state.value)}
                                                disabled={savingCompanySettings || !isXeroConnected}
                                                onClick={() => handleFridayInvoiceOptionToggle("xero", state.value)}
                                            >
                                                {state.label}
                                            </TogglePill>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4">
                                <DropdownSection
                                    title={`Users with weekly invoice permission (${weeklyInvoiceUsers.length})`}
                                    defaultOpen={false}
                                    titleStyle="text-sm font-semibold"
                                    customMainStyle="!shadow-none !border-gray-100"
                                    customClickableStyle="!px-4 !py-3"
                                    customContainerStyle="!p-0 !bg-white"
                                >
                                    <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
                                        {loadingWeeklyInvoiceUsers ? (
                                            <div className="p-4 text-sm font-semibold text-gray-400">
                                                Loading users...
                                            </div>
                                        ) : weeklyInvoiceUsers.length === 0 ? (
                                            <div className="p-4 text-sm font-semibold text-gray-400">
                                                No users currently have this permission.
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-gray-100">
                                                {weeklyInvoiceUsers.map((user) => (
                                                    <div
                                                        key={user.id}
                                                        className="flex items-center justify-between gap-4 px-4 py-3"
                                                    >
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <div className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 overflow-hidden shrink-0">
                                                                {user.avatar_url ? (
                                                                    <img
                                                                        className="w-full h-full object-cover"
                                                                        src={user.avatar_url}
                                                                        alt=""
                                                                    />
                                                                ) : null}
                                                            </div>

                                                            <div className="min-w-0">
                                                                <p className="text-sm font-semibold text-zinc-800 truncate">
                                                                    {user.full_name || user.username || "Unnamed User"}
                                                                </p>
                                                                <p className="text-xs text-gray-500 truncate">
                                                                    {user.email}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-semibold text-gray-500 capitalize shrink-0">
                                                            {String(user.role || "").replaceAll("_", " ")}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </DropdownSection>
                            </div>
                        </DropdownSection>

                        <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <h3 className="text-base font-semibold text-zinc-900">
                                        Driver Files
                                    </h3>

                                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                                        Allow office users to upload files that assigned drivers can
                                        view from their delivery assignment page.
                                    </p>

                                    <p className="text-xs text-gray-400 mt-2">
                                        Existing Driver Files remain visible after this setting is disabled.
                                    </p>
                                </div>

                                <TogglePill
                                    selected={enableDriverFiles}
                                    disabled={savingCompanySettings}
                                    onClick={() => handleToggleDriverFiles(!enableDriverFiles)}
                                    tone="green"
                                >
                                    {enableDriverFiles ? "Enabled" : "Disabled"}
                                </TogglePill>
                            </div>
                        </div>
                    </div>
                </SectionCard>
            )}


            {isSuperAdmin && (
                <SectionCard
                    title="Permissions"
                    description="Set permission behaviour across users."
                    icon={<FiLock />}
                >

                    <div className="space-y-5">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                            <label className="rounded-2xl bg-gray-50 border border-gray-100 p-4 flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={allowAnyUserToActAsEstimator}
                                    onChange={(e) => handleToggleAllowAnyUserEstimator(e.target.checked)}
                                    className="mt-1"
                                />

                                <div>
                                    <p className="text-sm font-semibold text-zinc-800">
                                        Allow any user to act as estimator
                                    </p>

                                    <p className="text-xs text-gray-500 mt-1">
                                        When enabled, users can access estimator actions even if their role is not estimator.
                                    </p>
                                </div>
                            </label>


                        </div>
                        <DropdownSection
                            title="Restricted folder upload roles"
                            subtext="Choose which roles can upload files to Quotes & Invoices and Layouts & Comps."
                            defaultOpen={false}
                            titleStyle="text-sm font-semibold"
                            subtextStyle="text-xs text-gray-500"
                            customMainStyle="!shadow-none !border-gray-100"
                            customClickableStyle="!px-4 !py-3"
                            customContainerStyle="!p-4 !bg-gray-50"
                        >
                            <div className="space-y-4">
                                {RESTRICTED_UPLOAD_FOLDERS.map(folderName => (
                                    <div
                                        key={folderName}
                                        className="rounded-2xl bg-white border border-gray-100 p-4"
                                    >
                                        <p className="text-sm font-semibold text-zinc-800">
                                            {folderName}
                                        </p>

                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {ROLE_OPTIONS.map(role => {
                                                const selected = (restrictedFolderUploadRoles[folderName] || [])
                                                    .includes(role.key);

                                                return (
                                                    <button
                                                        key={`${folderName}-${role.key}`}
                                                        type="button"
                                                        disabled={savingCompanySettings}
                                                        onClick={() => toggleRestrictedFolderUploadRole(folderName, role.key)}
                                                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${selected
                                                            ? "border-zinc-900 bg-zinc-900 text-white"
                                                            : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                                                            } disabled:opacity-60`}
                                                    >
                                                        {selected ? "On" : "Off"} · {role.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </DropdownSection>

                    </div>



                </SectionCard>
            )}

            {isSuperAdmin && (
                <PricingQuotationSettings
                    company={company}
                    userProfile={userProfile}
                    users={companyUsers}
                    setUsers={setCompanyUsers}
                    updateCompanySettings={updateCompanySettings}
                    savingCompanySettings={savingCompanySettings}
                    isSuperAdmin={isSuperAdmin}
                    setError={setError}
                />
            )}

            <SectionCard
                title="Invoice settings"
                description="Choose how invoice statuses are displayed across jobs, schedules, and dashboards."
                icon={<FiInfo />}
            >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="rounded-2xl bg-gray-50 border border-gray-100 p-4">
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                            Display source
                        </label>

                        <select
                            value={invoiceDisplayMode}
                            disabled={savingCompanySettings}
                            onChange={(e) => handleInvoiceDisplayModeChange(e.target.value)}
                            className="mt-2 w-full appearance-none rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-zinc-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/10 disabled:opacity-60"
                        >
                            <option value="notify">Notify Invoices</option>
                            <option value="xero" disabled={!isXeroConnected}>
                                Xero Invoices {!isXeroConnected ? "(requires Xero)" : ""}
                            </option>
                            <option value="hybrid" disabled={!isXeroConnected}>
                                Hybrid {!isXeroConnected ? "(requires Xero)" : ""}
                            </option>
                        </select>

                        {invoiceDisplayMode === "hybrid" && (
                            <TextTooltip
                                text="Hybrid will show the Xero invoice status when a matching Xero invoice exists. If no Xero invoice is linked, Notify invoice status will be shown instead."
                                customStyle="w-full h-full !inline"
                                customTextStyle="w-96 h-auto !whitespace-normal !break-normal"
                            >
                                <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-gray-500">
                                    <FiInfo className="text-zinc-500" />
                                    Hybrid mode info
                                </div>
                            </TextTooltip>
                        )}
                    </div>

                    <div className={`rounded-2xl border p-4 relative overflow-hidden ${isXeroConnected
                        ? "bg-gray-50 border-gray-100"
                        : "bg-gray-100 border-gray-200"
                        }`}>
                        {!isXeroConnected && (
                            <div className="absolute inset-0 z-10 bg-gray-100/70 backdrop-blur-[1px] flex items-start justify-center pt-4">
                                <span className="inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 px-4 py-1.5 text-xs font-semibold text-gray-500 shadow-sm">
                                    <FiLock />
                                    Xero required
                                </span>
                            </div>
                        )}

                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                            Xero status labels
                        </label>

                        <select
                            value={invoiceTextMode}
                            disabled={savingCompanySettings || !isXeroConnected}
                            onChange={(e) => handleInvoiceTextModeChange(e.target.value)}
                            className="mt-2 w-full appearance-none rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-zinc-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/10 disabled:opacity-60"
                        >
                            <option value="friendly">Altered Notify Names</option>
                            <option value="xero" disabled={!isXeroConnected}>
                                Original Xero Names {!isXeroConnected ? "(requires Xero)" : ""}
                            </option>
                        </select>

                        <p className="mt-3 text-xs text-gray-400 leading-relaxed">
                            {invoiceTextMode === "friendly"
                                ? "Shows easier labels like Needs Approval, Invoiced, and Cancelled."
                                : "Shows original Xero labels like Submitted, Authorised, and Voided."}
                        </p>
                    </div>
                </div>
            </SectionCard>

            <SectionCard
                title="Xero integration"
                description="Connect this company to Xero and sync invoice statuses by matching invoice references with Notify department IDs."
                icon={<span className="font-semibold">X</span>}
                right={
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${isXeroConnected
                        ? "bg-green-50 text-green-700"
                        : "bg-amber-50 text-amber-700"
                        }`}>
                        {isXeroConnected ? <FiCheckCircle /> : <FiClock />}
                        {isXeroConnected ? "Connected" : "Not connected"}
                    </span>
                }
            >
                <div className="space-y-5">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            type="button"
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-zinc-700 hover:bg-gray-50 transition"
                            onClick={() => setShowXeroGuide(true)}
                        >
                            View setup guide
                            <FiExternalLink />
                        </button>

                        <button
                            type="button"
                            onClick={handleConnectXero}
                            disabled={!canConnectXero || !company?.id || !userProfile?.id}
                            className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition shadow-sm ${canConnectXero
                                ? "bg-zinc-900 text-white hover:bg-zinc-800"
                                : "bg-gray-100 text-gray-400 cursor-not-allowed"
                                }`}
                        >
                            {canConnectXero ? (
                                <>
                                    {isXeroConnected ? "Reconnect Xero" : "Connect Xero"}
                                    <FiArrowRight />
                                </>
                            ) : (
                                <>
                                    <FiLock />
                                    Permission required
                                </>
                            )}
                        </button>

                        {isXeroConnected && (
                            <button
                                type="button"
                                onClick={handleDisconnectXero}
                                disabled={!canConnectXero || disconnectingXero}
                                className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition ${canConnectXero
                                    ? "border-red-200 text-red-700 bg-red-50 hover:bg-red-100"
                                    : "border-gray-200 text-gray-400 bg-gray-100 cursor-not-allowed"
                                    }`}
                            >
                                <FiTrash2 />
                                {disconnectingXero ? "Disconnecting..." : "Disconnect"}
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div className="lg:col-span-2 rounded-2xl border border-gray-100 bg-gray-50 p-4">
                            <h3 className="text-sm font-semibold text-zinc-800">
                                Connection details
                            </h3>

                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <InfoRow label="Xero organisation" value={xeroConnection?.xero_tenant_name || "Not connected yet"} />
                                <InfoRow label="Connection state" value={isXeroConnected ? "Connected and active" : "Waiting for setup"} />
                                <InfoRow label="Connected at" value={formatDateTime(xeroConnection?.created_at)} />
                                <InfoRow label="Last updated" value={formatDateTime(xeroConnection?.updated_at)} />
                                <InfoRow label="Token expires" value={formatDateTime(xeroConnection?.expires_at)} />
                                <InfoRow label="Matching method" value="Xero Reference → Department ID" />
                            </div>
                        </div>

                        <div className="rounded-2xl border border-zinc-800 bg-zinc-950 text-white p-4">
                            <h3 className="text-sm font-semibold">
                                Matching flow
                            </h3>

                            <div className="mt-4 space-y-3">
                                <StepItem number="1" text="Xero invoice has Reference: N123R1" />
                                <StepItem number="2" text="Notify finds the matching department." />
                                <StepItem number="3" text="Invoice status updates in Notify." />
                            </div>

                            <div className="mt-4 rounded-2xl bg-white/10 border border-white/10 p-3">
                                <p className="text-xs text-zinc-400">Required Xero habit</p>
                                <p className="text-sm font-semibold mt-1">
                                    Enter the Notify department ID in the Xero Reference field.
                                </p>
                            </div>
                        </div>
                    </div>

                    {!canConnectXero && (
                        <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4 text-sm text-amber-800">
                            You can view this integration, but you cannot connect or reconnect Xero.
                            Required permissions: <b>CONNECT_INTEGRATIONS</b> and{" "}
                            <b>CONNECT_INTEGRATION_XERO</b>.
                        </div>
                    )}
                </div>
            </SectionCard>

            <IntegrationXeroSetup
                open={showXeroGuide}
                onClose={() => setShowXeroGuide(false)}
            />
        </div>
    );
};

const InfoRow = ({ label, value }) => (
    <div className="rounded-2xl bg-white border border-gray-100 p-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
            {label}
        </p>
        <p className="mt-2 text-sm font-semibold text-zinc-800 break-words">
            {value || "—"}
        </p>
    </div>
);

const StepItem = ({ number, text }) => (
    <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-full bg-white text-zinc-950 flex items-center justify-center text-xs font-semibold shrink-0">
            {number}
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">{text}</p>
    </div>
);

const formatDateTime = (value) => {
    if (!value) return "—";

    try {
        return new Date(value).toLocaleString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return value;
    }
};

export default Settings;